import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';

export default function Ledger() {
  const [dateRange, setDateRange] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedParty, setSelectedParty] = useState(null); // { type: 'customer'/'supplier', id: number, name: string }
  const [partySearch, setPartySearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [size] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [sortBy] = useState('transactionDate');
  const [sortDir] = useState('desc');
  const navigate = useNavigate();

  // Calculate total balance
  const totalBalance = transactions.reduce((sum, transaction) => {
    return transaction.type === 'credit'
      ? sum + transaction.amount
      : sum - transaction.amount;
  }, 0);

  // Fetch customers
  const fetchCustomers = async (search = '') => {
    try {
      const token = localStorage.getItem('token');
      const res = await api.get(`/customers?page=0&size=100&sortBy=name&sortDir=asc&search=${encodeURIComponent(search)}`, {
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      });
      const customerList = res.data.data?.content?.map(customer => ({
        id: customer.id,
        name: customer.name || 'Unknown',
        phone: customer.phone || '',
        type: 'customer',
      })) || [];
      setCustomers(customerList);
    } catch (err) {
      console.error('Fetch customers error:', err);
      if (err.response?.status === 401) {
        navigate('/login');
      }
      setError(err.response?.data?.message || 'Failed to fetch customers');
    }
  };

  // Fetch suppliers
  const fetchSuppliers = async (search = '') => {
    try {
      const token = localStorage.getItem('token');
      const res = await api.get(`/suppliers?page=0&size=100&sortBy=name&sortDir=asc&search=${encodeURIComponent(search)}`, {
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      });
      const supplierList = res.data.data?.content?.map(supplier => ({
        id: supplier.id,
        name: supplier.name || 'Unknown',
        phone: supplier.phone || '',
        type: 'supplier',
      })) || [];
      setSuppliers(supplierList);
    } catch (err) {
      console.error('Fetch suppliers error:', err);
      if (err.response?.status === 401) {
        navigate('/login');
      }
      setError(err.response?.data?.message || 'Failed to fetch suppliers');
    }
  };

  // Fetch transactions
  const fetchTransactions = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      let startDate, endDate;
      const today = new Date();
      const formatDate = (date) => date.toISOString().split('T')[0];

      if (dateRange === 'today') {
        startDate = formatDate(today);
        endDate = startDate;
      } else if (dateRange === 'week') {
        startDate = formatDate(new Date(today.getFullYear(), today.getMonth(), today.getDate() - today.getDay()));
        endDate = formatDate(new Date(today.getFullYear(), today.getMonth(), today.getDate() + (6 - today.getDay())));
      } else if (dateRange === 'month') {
        startDate = formatDate(new Date(today.getFullYear(), today.getMonth(), 1));
        endDate = formatDate(new Date(today.getFullYear(), today.getMonth() + 1, 0));
      } else if (dateRange === 'year') {
        startDate = formatDate(new Date(today.getFullYear(), 0, 1));
        endDate = formatDate(new Date(today.getFullYear(), 11, 31));
      } else if (dateRange === 'custom' && customStartDate && customEndDate) {
        startDate = customStartDate;
        endDate = customEndDate;
      }

      let url = `/transactions?page=${page}&size=${size}&sortBy=${sortBy}&sortDir=${sortDir}`;
      if (startDate && endDate) {
        url += `&startDate=${startDate}&endDate=${endDate}`;
      }
      if (searchTerm) {
        url += `&search=${encodeURIComponent(searchTerm)}`;
      }
      if (selectedParty) {
        url += `&${selectedParty.type}Id=${selectedParty.id}`;
      }

      console.log('Fetching transactions with URL:', url);
      const res = await api.get(url, {
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      });
      const transactionList = res.data.data?.content?.map(transaction => ({
        id: transaction.id,
        date: transaction.transactionDate,
        description: transaction.notes || transaction.category || '-',
        type: transaction.type === 'PAYMENT_RECEIVED' || transaction.type === 'INCOME' ? 'credit' : 'debit',
        amount: transaction.amount,
        balance: transaction.runningBalance || 0,
        customerName: transaction.customerName || '-',
        supplierName: transaction.supplierName || '-',
      })) || [];
      console.log('Fetched transactions:', transactionList);
      setTransactions(transactionList);
      setTotalPages(res.data.data?.totalPages || 1);
    } catch (err) {
      console.error('Fetch transactions error:', err);
      if (err.response?.status === 401) {
        navigate('/login');
      }
      setError(err.response?.data?.message || 'Failed to fetch transactions');
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch customers and suppliers on mount and when partySearch changes
  useEffect(() => {
    fetchCustomers(partySearch);
    fetchSuppliers(partySearch);
  }, [partySearch]);

  // Fetch transactions when dependencies change
  useEffect(() => {
    fetchTransactions();
  }, [page, dateRange, customStartDate, customEndDate, searchTerm, selectedParty]);

  // Handle export
  const handleExport = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await api.get('/transactions/export', {
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'transactions_export.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      alert('Export successful!');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to export transactions');
    }
  };

  // Filtered parties for dropdown
  const filteredParties = [
    ...customers.map(c => ({ ...c, display: `${c.name} (Customer, ${c.phone || '-'})` })),
    ...suppliers.map(s => ({ ...s, display: `${s.name} (Supplier, ${s.phone || '-'})` })),
  ].filter(party =>
    party.name.toLowerCase().includes(partySearch.toLowerCase()) ||
    (party.phone && party.phone.includes(partySearch))
  );

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Ledger</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          View and manage your business transactions
        </p>
      </div>

      <div className="mb-6 flex flex-col md:flex-row gap-4 justify-between items-start md:items-center">
        <div className="flex flex-col md:flex-row gap-4">
          <select
            value={dateRange}
            onChange={(e) => { setDateRange(e.target.value); setPage(0); }}
            className="block w-40 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="year">This Year</option>
            <option value="custom">Custom Range</option>
          </select>

          {dateRange === 'custom' && (
            <div className="flex gap-4">
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => { setCustomStartDate(e.target.value); setPage(0); }}
                className="block w-40 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => { setCustomEndDate(e.target.value); setPage(0); }}
                className="block w-40 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
            </div>
          )}

          <input
            type="text"
            placeholder="Search customers/suppliers..."
            value={partySearch}
            onChange={(e) => { setPartySearch(e.target.value); setPage(0); }}
            className="block w-full md:w-64 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          />

          <select
            value={selectedParty ? `${selectedParty.type}:${selectedParty.id}` : ''}
            onChange={(e) => {
              const [type, id] = e.target.value.split(':');
              const party = filteredParties.find(p => p.type === type && p.id === parseInt(id));
              setSelectedParty(party ? { type, id: parseInt(id), name: party.name } : null);
              setPage(0);
            }}
            className="block w-full md:w-64 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          >
            <option value="">All Customers/Suppliers</option>
            {filteredParties.map(party => (
              <option key={`${party.type}:${party.id}`} value={`${party.type}:${party.id}`}>
                {party.display}
              </option>
            ))}
          </select>

          <input
            type="text"
            placeholder="Search transactions..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
            className="block w-full md:w-64 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          />
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleExport}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Export
          </button>
          <button
            onClick={() => window.print()}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Print
          </button>
        </div>
      </div>

      <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Balance</p>
            <p className={`text-2xl font-bold ${totalBalance >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              ₹{totalBalance.toLocaleString('en-IN')}
            </p>
          </div>
        </div>
      </div>

      {error && <div className="mb-4 text-red-600">{error}</div>}
      {loading ? (
        <div className="text-gray-600 dark:text-gray-400">Loading...</div>
      ) : (
        <>
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Date
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Party
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Description
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Debit (₹)
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Credit (₹)
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Balance (₹)
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {transactions.map((transaction) => (
                    <tr key={transaction.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">
                        {new Date(transaction.date).toLocaleDateString('en-IN')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">
                        {transaction.customerName !== '-' ? transaction.customerName : transaction.supplierName}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-300">
                        {transaction.description}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-gray-300">
                        {transaction.type === 'debit' ? transaction.amount.toLocaleString('en-IN') : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-gray-300">
                        {transaction.type === 'credit' ? transaction.amount.toLocaleString('en-IN') : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900 dark:text-gray-300">
                        {transaction.balance.toLocaleString('en-IN')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-center mt-4 gap-2">
            <button
              disabled={page === 0}
              onClick={() => setPage(page - 1)}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              Prev
            </button>
            <span>Page {page + 1} of {totalPages}</span>
            <button
              disabled={page + 1 >= totalPages}
              onClick={() => setPage(page + 1)}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}